// One notification = one card. The card owns the border, radius and elevation
// so the list reads as discrete objects rather than a ruled table; the button
// and the detail panel sit inside it and never draw their own edges.
.root {
  list-style: none;
}

// The card reacts to nothing INTERACTIVE: identical whether hovered, focused or
// expanded — no hover border, no expanded tint, no accent stripe. Expanding a
// card changes its height and nothing else; cursor is the sole affordance.
// Read/unread is the one piece of state it carries (see `.card-unread`).
.card {
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: var(--drawer-card-radius);
  box-shadow: var(--shadow-card);
  // Clips the panel while it animates from 0fr, so nothing escapes the radius.
  overflow: hidden;
}

// Unread cards sit on the shared subtle-grey surface token, one step off the
// page — enough to pick the unread block out while scanning, not enough to read
// as a highlight or a different kind of object. It backs up the dot rather than
// replacing it: the glyph stays the primary signal, so the state is never
// carried by a 4% tint alone.
.card-unread {
  background: var(--color-secondary);
}

// Collapsed line: indicator · title · timestamp · chevron.
.row {
  align-items: center;
  background: transparent;
  border: none;
  cursor: pointer;
  display: grid;
  // Fixed first and last columns keep every title and chevron on the same
  // vertical line regardless of how long the timestamp is.
  grid-template-columns: 18px minmax(0, 1fr) auto 16px;
  gap: 10px;
  padding: 13px 14px;
  text-align: left;
  width: 100%;

  // `:focus-visible`, NOT `:focus`/`:focus-within`: the browser only matches it
  // for keyboard focus, so clicking a card leaves no ring behind while a
  // keyboard user still gets a visible target. Removing this outright would
  // leave the drawer unusable without a mouse (WCAG 2.4.7), so it stays.
  &:focus-visible {
    outline: 2px solid var(--color-focus);
    outline-offset: -2px;
  }
}

.indicator {
  align-items: center;
  display: flex;
  height: 18px;
  justify-content: center;
  width: 18px;
}

.dot {
  background: var(--color-link);
  border-radius: 999px;
  height: 8px;
  width: 8px;
}

// Read cards are de-emphasised, never disabled-looking: a muted double check,
// the same idiom messaging apps use for "seen".
.read-icon {
  color: var(--color-placeholder);
  height: 15px;
  width: 15px;
}

.headline {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// No `tabular-nums`: the stamps are right-aligned and vary in length
// ("Today" vs "31 Jul"), so tabular figures buy no column alignment while
// giving the colon a full digit advance — "12 : 27" instead of "12:27".
.time {
  color: var(--color-muted-fg);
  flex-shrink: 0;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}

.chevron {
  color: var(--color-placeholder);
  height: 16px;
  transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
  width: 16px;
}

// Driven by the state attribute rather than a modifier class: with the card now
// stateless, the chevron is the only thing that reacts to expansion, and there
// is no second source of truth to keep in sync.
.row[aria-expanded='true'] .chevron {
  color: var(--color-muted);
  transform: rotate(180deg);
}

// Height animation without measuring: 0fr → 1fr on a grid row.
.panel {
  display: grid;
  grid-template-rows: 0fr;
  opacity: 0;
  transition:
    grid-template-rows 200ms cubic-bezier(0.16, 1, 0.3, 1),
    opacity 160ms ease;
}

.panel-open {
  grid-template-rows: 1fr;
  opacity: 1;
}

.panel-inner {
  min-height: 0;
  // No padding here: a 0fr grid row collapses the ROW, but padding on the
  // clipped element still occupies space, leaving a sliver under every
  // collapsed card. Padding belongs to the inner content instead.
  overflow: hidden;
}

.panel-content {
  border-top: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0 14px;
  // Left-aligned with the collapsed TITLE (18px indicator + 10px gap), so the
  // expanded text continues the same optical column instead of stepping out.
  padding: 12px 0 14px 28px;
}

.subject {
  overflow-wrap: anywhere;
}

.actions {
  display: flex;
  gap: 8px;
  padding-top: 6px;
}

@media (max-width: 560px) {
  .row {
    // The chevron column collapses; the fixed columns stay so the title and
    // timestamp keep their alignment on a narrow drawer.
    grid-template-columns: 18px minmax(0, 1fr) auto;
  }

  .chevron {
    display: none;
  }

  // The indent is NOT dropped here: the expanded text must keep sharing the
  // collapsed title's column, and 28px of a 390px drawer is affordable.

  .actions button {
    // Comfortable tap target on touch.
    min-height: 40px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chevron,
  .panel {
    transition: none;
  }
}
